"use client";

import { createContext, useContext, useEffect } from "react";
import { useAppSelector } from "@/store";
import { setDisplayTimezone } from "@/lib/utils";

const TimezoneContext = createContext<string>("Europe/Berlin");

/**
 * Reads the agency's timezone settings from the Redux store and:
 * 1. Provides the resolved display timezone via React context
 * 2. Syncs it into the module-level `_displayTimezone` in utils.ts so all
 *    formatDate/formatDateTime/formatTime calls use the right timezone
 *    without needing to thread a parameter through every call site.
 */
export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  const agency = useAppSelector((state) => state.user.currentUser?.agency);
  const contextTz = agency?.context_timezone || "Europe/Berlin";
  const useBrowser = agency?.use_browser_timezone || false;

  const displayTz = useBrowser
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : contextTz;

  // Sync to the module-level singleton so non-React code (utils.ts) picks it up
  useEffect(() => {
    setDisplayTimezone(displayTz);
  }, [displayTz]);

  return (
    <TimezoneContext.Provider value={displayTz}>
      {children}
    </TimezoneContext.Provider>
  );
}

/**
 * Hook to read the current display timezone inside React components.
 * Most components don't need this — they just call formatDate/formatDateTime
 * which read the timezone from the module-level singleton. Use this hook only
 * when you need the raw timezone string (e.g. for the settings page display).
 */
export function useDisplayTimezone(): string {
  return useContext(TimezoneContext);
}
